/**
 * Utilities for building SVG paths through waypoints using Catmull-Rom splines.
 */

/**
 * Snap a value to the nearest grid point.
 */
export function snapToGrid(value, gridSize = 10) {
    return Math.round(value / gridSize) * gridSize;
}

/**
 * Find which segment of a polyline is closest to a given point.
 * Returns the index after which to insert a new waypoint.
 * @param {Array<{x:number, y:number}>} points - All points (source, waypoints, target)
 * @param {{x:number, y:number}} click - The click position
 * @returns {number} insertion index into the waypoints array (0 = before first wp)
 */
export function closestSegmentIndex(points, click) {
    let minDist = Infinity;
    let bestIdx = 0;
    for (let i = 0; i < points.length - 1; i++) {
        const d = distToSegment(click, points[i], points[i + 1]);
        if (d < minDist) {
            minDist = d;
            bestIdx = i;
        }
    }
    // bestIdx is the index in the full points array (which includes source at 0).
    // The waypoints array starts at index 1, so insertion index = bestIdx.
    // E.g. if closest segment is between source(0) and wp0(1), insert at index 0 in waypoints.
    return bestIdx;
}

function distToSegment(p, a, b) {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return Math.hypot(p.x - a.x, p.y - a.y);
    let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const projX = a.x + t * dx;
    const projY = a.y + t * dy;
    return Math.hypot(p.x - projX, p.y - projY);
}

/**
 * Compute an initial offset point exiting a handle in the correct direction.
 */
function handleOffset(x, y, position, dist = 30) {
    switch (position) {
        case 'top':    return { x, y: y - dist };
        case 'bottom': return { x, y: y + dist };
        case 'left':   return { x: x - dist, y };
        case 'right':  return { x: x + dist, y };
        default:       return { x, y };
    }
}

/**
 * Build an SVG path string through waypoints using Catmull-Rom → cubic bezier conversion.
 * The curve passes exactly through every waypoint.
 *
 * @param {number} sourceX
 * @param {number} sourceY
 * @param {string} sourcePosition - 'top'|'bottom'|'left'|'right'
 * @param {number} targetX
 * @param {number} targetY
 * @param {string} targetPosition
 * @param {Array<{x:number, y:number}>} waypoints
 * @returns {string} SVG path d attribute
 */
export function buildWaypointPath(sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, waypoints) {
    // Build full point list with handle-direction offsets for clean entry/exit
    const srcOff = handleOffset(sourceX, sourceY, sourcePosition);
    const tgtOff = handleOffset(targetX, targetY, targetPosition);

    const points = [
        { x: sourceX, y: sourceY },
        srcOff,
        ...waypoints,
        tgtOff,
        { x: targetX, y: targetY },
    ];

    // Straight line segments through all points
    let d = `M ${points[0].x},${points[0].y}`;
    for (let i = 1; i < points.length; i++) {
        d += ` L ${points[i].x},${points[i].y}`;
    }
    return d;
}

/**
 * Compute the visual stroke width for a pipe-style edge given a diameter in mm.
 * Maps real-world diameter to a visual range: min 4px, max 20px.
 */
export function diameterToStrokeWidth(diameterMm) {
    const d = parseFloat(diameterMm) || 50;
    // Log scale: 10mm → 4px, 50mm → 8px, 200mm → 14px, 500mm → 20px
    return Math.max(4, Math.min(20, 4 + 5 * Math.log10(d / 10)));
}
